import React, { useState, useEffect, useContext } from "react";
import "../../App.css"; // Import global styles
import { FaSearch, FaPlus } from "react-icons/fa"; // Import icons
import AddProject from "./AddProject"; // Import AddProject.jsx file
import { UserContext } from "../context/UserContext"; // Import UserContext for user authentication

const ProjectList = ({ onProjectClick }) => {
  const [searchQuery, setSearchQuery] = useState(""); // State for search input
  const [isAddProjectOpen, setIsAddProjectOpen] = useState(false); // State to toggle AddProject popup
  const [projects, setProjects] = useState([]); // Initialize as an empty array
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024); // State to track screen size
  const { isLoggedIn, emailId, phoneNo } = useContext(UserContext); // Fetch user login, email, and phone number

  // Detect screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024); // Update mobile state based on screen width
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize); // Cleanup on unmount
  }, []);

  const handleProjectSelection = (project) => {
    try {
      localStorage.setItem("selectedProjectId", project.project_id);
      localStorage.setItem("selectionTimestamp", Date.now());
      // Dispatch custom event for immediate state update
      window.dispatchEvent(new CustomEvent('projectSelected', {
        detail: { projectId: project.project_id }
      }));
      onProjectClick("project"); // Pass project type
    } catch (error) {
      console.error("Error selecting project:", error);
    }
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch("/api/projectlist?type=project"); // Add type=project parameter
        if (response.ok) {
          const data = await response.json();
          setProjects(data);
        } else {
          console.error("Failed to fetch projects");
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
    const intervalId = setInterval(fetchProjects, 5000);
    return () => clearInterval(intervalId);
  }, []);

  const filteredProjects = projects.filter((project) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      project.project_title?.toLowerCase().includes(searchLower) ||
      project.category?.toLowerCase().includes(searchLower) ||
      project.description?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="project-list-container" style={{ 
      padding: "10px",
      height: "100%",
      overflow: "hidden"
    }}>
      {/* Search bar and Add button */}
      <div
        className="search-add-container"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
          backgroundColor: "#fff",
          zIndex: 1,
          padding: "10px 0"
        }}
      >
        {/* Search Bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            position: "relative",
            width: "300px",
            height: "40px",
            background: "linear-gradient(to right, #007bff, #00d4ff)",
            borderRadius: "50px",
            padding: "10px",
          }}
        >
          <FaSearch
            style={{
              fontSize: "18px",
              color: "white",
              marginRight: "10px",
            }}
          />
          <input
            type="text"
            placeholder="Type to search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
              color: "#fff",
              fontSize: "14px",
              caretColor: "#fff",
            }}
            className="search-input-placeholder"
          />
        </div>

        {/* Add Button - Only show when user is logged in */}
        {isLoggedIn && emailId && phoneNo && (
          <button
            onClick={() => setIsAddProjectOpen(true)}
            style={{
              background: "linear-gradient(to right, #007bff, #00d4ff)",
              color: "#fff",
              border: "none",
              padding: "10px",
              borderRadius: "50%",
              fontSize: "18px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            <FaPlus />
          </button>
        )}
      </div>

      {/* Project List - Scrollable */}
      <div
        className="project-list"
        style={{
          display: "grid",
          gap: "20px",
          maxHeight: "calc(100vh - 180px)",
          overflowY: "auto",
          padding: "10px 10px 100px 0",
          marginRight: "5px"
        }}
      >
        {isLoading ? (
          <p>Loading projects...</p>
        ) : (
          filteredProjects.map((project) => (
            <div
              key={project.project_id}
              className="project-card"
              onClick={() => handleProjectSelection(project)}
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row", // Change layout based on screen size
                gap: "15px",
                padding: "10px",
                backgroundColor: "#fff",
                borderRadius: "10px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                alignItems: isMobile ? "center" : "flex-start", // Center alignment for mobile
                width: isMobile ? "100%" : "380px", // Full width on mobile
                cursor: "pointer",
              }}
            >
              {/* Thumbnail */}
              <div
                className="project-thumbnail"
                style={{
                  width: isMobile ? "100%" : "150px", // Full width on mobile
                  height: isMobile ? "auto" : "100px", // Auto height on mobile
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {project.image_url?.match(/\.(jpeg|jpg|png|gif)$/i) ? (
                  <img
                    src={project.image_url}
                    alt={project.project_title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : project.image_url?.match(/\.(mp4|mov|avi|mkv)$/i) ? (
                  <video
                    controls
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  >
                    <source src={project.image_url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <p
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "#e0e0e0",
                      color: "#555",
                      fontSize: "12px",
                      textAlign: "center",
                    }}
                  >
                    No media available
                  </p>
                )}
              </div>

              {/* Project Info */}
              <div
                className="project-info"
                style={{
                  flex: "1",
                  marginTop: isMobile ? "10px" : "0", // Add margin above for mobile
                  textAlign: isMobile ? "center" : "left", // Center align text on mobile
                }}
              >
                <h5
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginBottom: "4px",
                  }}
                >
                  {project.project_title}
                </h5>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#555",
                    margin: "0 0 8px",
                    fontWeight: "bold",
                  }}
                >
                  #{project.category}
                </p>
                <div
                  style={{
                    marginTop: "5px",
                  }}
                >
                  <div
                    style={{
                      height: "10px",
                      background: "#e0e0e0",
                      borderRadius: "5px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${Math.min(
                          (project.total_raised / project.target_amount) * 100,
                          100
                        )}%`,
                        background:
                          "linear-gradient(to right, #007bff, #00d4ff)",
                        borderRadius: "5px",
                        transition: "width 0.3s ease-in-out",
                      }}
                    ></div>
                  </div>
                  <p
                    style={{
                      marginTop: "5px",
                      fontSize: "12px",
                      color: "#777",
                    }}
                  >
                    ${project.total_raised || 0} raised of $
                    {project.target_amount}
                  </p>
                </div>
                <p
                  style={{
                    margin: "5px 0 0",
                    fontSize: "12px",
                    color: "#777",
                  }}
                >
                  End Date: {project.end_date}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* AddProject Popup */}
      {isAddProjectOpen && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            zIndex: 1000,
          }}
        >
          <AddProject onClose={() => setIsAddProjectOpen(false)} />
        </div>
      )}
    </div>
  );
};

export default ProjectList;
